/* eslint-disable jsx-a11y/alt-text */
import { Box, Grid, Typography } from '@material-ui/core';
import React from 'react';
import { currencyFormatter, simpleDateFormatter2 } from '../Shared/colorFormat';
import './index.css';

const ADVANCE_PAYMENT = 78;
const ADVANCE_RECEIPT = 77;

const getOpTypeLabel = (opType: number, isRTL: boolean) => {
  if (opType === ADVANCE_PAYMENT) {
    return isRTL ? 'دفع سلفة' : 'Advance Paid';
  }
  if (opType === ADVANCE_RECEIPT) {
    return isRTL ? 'قبض سلفة' : 'Advance Received';
  }
  return isRTL ? 'غير معروف' : 'Unknown';
};

const getEmployeeName = (item: any, isRTL: boolean) => {
  if (isRTL) {
    return item?.employeeNameAr || item?.employeeName || '-';
  }
  return item?.employeeName || item?.employeeNameAr || '-';
};

export class AdvanceReportPrint extends React.PureComponent<any, any> {
  renderHeader = (company: any) => (
    <Grid item xs={12}>
      {company?.header ? (
        <img
          src={company?.header}
          alt={company?.name}
          height="auto"
          width="100%"
          style={{
            objectFit: 'contain',
          }}
        />
      ) : (
        <Typography
          variant="h5"
          style={{ textAlign: 'center', fontWeight: 'bold' }}
        >
          {company?.name || ''}
        </Typography>
      )}
    </Grid>
  );

  renderTitle = (printData: any) => {
    const { isRTL, start, end, employee } = printData;
    return (
      <Grid item xs={12}>
        <Grid container spacing={0}>
          <Grid item xs={12} style={{ marginBottom: 20, marginTop: 20 }}>
            <Typography style={{ textAlign: 'center' }} variant="h4">
              {isRTL ? 'تقرير السلف للموظفين' : 'Employee Advances Report'}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="subtitle1">
              {isRTL ? 'الفترة' : 'Period'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1">{simpleDateFormatter2(start)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1">{simpleDateFormatter2(end)}</Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="subtitle1">
              {isRTL ? 'الموظف' : 'Employee'}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
              {employee
                ? isRTL
                  ? employee?.nameAr || employee?.name
                  : employee?.name || employee?.nameAr
                : isRTL
                  ? 'الكل'
                  : 'All'}
            </Typography>
          </Grid>
          <Grid item xs={12} style={{ padding: 10 }}></Grid>
        </Grid>
      </Grid>
    );
  };

  getSummaryRows = (rows: any[], isRTL: boolean) => {
    const grouped = rows.reduce((acc: any, item: any) => {
      const employeeId = item?.employeeId || 'unknown';
      if (!acc[employeeId]) {
        acc[employeeId] = {
          employeeId,
          employeeName: getEmployeeName(item, isRTL),
          totalPaid: 0,
          totalReceived: 0,
        };
      }
      if (item?.opType === ADVANCE_PAYMENT) {
        acc[employeeId].totalPaid += item?.amount || 0;
      }
      if (item?.opType === ADVANCE_RECEIPT) {
        acc[employeeId].totalReceived += item?.amount || 0;
      }
      return acc;
    }, {});

    return Object.values(grouped).map((item: any) => ({
      ...item,
      balance: (item?.totalPaid || 0) - (item?.totalReceived || 0),
    }));
  };

  renderSummaryHeader = (isRTL: boolean) => {
    return (
      <tr style={{ fontSize: 12 }}>
        <th>{isRTL ? 'م' : '#'}</th>
        <th>{isRTL ? 'الموظف' : 'Employee'}</th>
        <th>{isRTL ? 'إجمالي السلف المدفوعة' : 'Total Advance Paid'}</th>
        <th>{isRTL ? 'إجمالي السلف المقبوضة' : 'Total Advance Received'}</th>
        <th>{isRTL ? 'الرصيد المتبقي' : 'Remaining Balance'}</th>
      </tr>
    );
  };

  renderSummaryRow = (item: any, index: number) => {
    return (
      <tr style={{ fontSize: 12 }}>
        <td style={{ textAlign: 'center' }}>{index + 1}</td>
        <td style={{ textAlign: 'center' }}>{item?.employeeName}</td>
        <td>{currencyFormatter({ value: item?.totalPaid })}</td>
        <td>{currencyFormatter({ value: item?.totalReceived })}</td>
        <td>{currencyFormatter({ value: item?.balance })}</td>
      </tr>
    );
  };

  renderSummaryTotals = (summaryRows: any[], isRTL: boolean) => {
    const totals = summaryRows.reduce(
      (acc: any, item: any) => {
        acc.paid += item?.totalPaid || 0;
        acc.received += item?.totalReceived || 0;
        acc.balance += item?.balance || 0;
        return acc;
      },
      { paid: 0, received: 0, balance: 0 },
    );

    return (
      <tr style={{ fontSize: 13, fontWeight: 'bold' }}>
        <td colSpan={2} style={{ textAlign: 'center' }}>
          {isRTL ? 'المجموع' : 'Total'}
        </td>
        <td>{currencyFormatter({ value: totals.paid })}</td>
        <td>{currencyFormatter({ value: totals.received })}</td>
        <td>{currencyFormatter({ value: totals.balance })}</td>
      </tr>
    );
  };

  renderDetailsHeader = (isRTL: boolean) => {
    return (
      <tr style={{ fontSize: 12 }}>
        <th>{isRTL ? 'التاريخ' : 'Date'}</th>
        <th>{isRTL ? 'النوع' : 'Type'}</th>
        <th>{isRTL ? 'رقم السند' : 'Document No'}</th>
        <th>{isRTL ? 'الموظف' : 'Employee'}</th>
        <th>{isRTL ? 'البيان' : 'Description'}</th>
        <th>{isRTL ? 'المبلغ' : 'Amount'}</th>
      </tr>
    );
  };

  renderDetailsRow = (item: any, isRTL: boolean) => {
    return (
      <tr style={{ fontSize: 12 }}>
        <td style={{ textAlign: 'center' }}>
          {simpleDateFormatter2(item?.time || item?.createdAt)}
        </td>
        <td style={{ textAlign: 'center' }}>
          {getOpTypeLabel(item?.opType, isRTL)}
        </td>
        <td style={{ textAlign: 'center' }}>{item?.docNo || '-'}</td>
        <td style={{ textAlign: 'center' }}>{getEmployeeName(item, isRTL)}</td>
        <td style={{ textAlign: 'center' }}>{item?.desc || '-'}</td>
        <td>{currencyFormatter({ value: item?.amount })}</td>
      </tr>
    );
  };

  renderDivider = (x: number) => (
    <Grid item xs={12}>
      <Box m={x}></Box>
    </Grid>
  );

  render() {
    const { company, printData } = this.props;
    const { isRTL, rows } = printData;
    const summaryRows = this.getSummaryRows(rows || [], isRTL);

    return (
      <table className="report-container">
        <thead className="report-header">
          <tr>
            <th className="report-header-cell">
              <div className="header-info">{this.renderHeader(company)}</div>
            </th>
          </tr>
        </thead>
        <tbody className="report-content">
          <tr>
            <td className="report-content-cell">
              <div className="main">
                <Box style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                  <Grid container spacing={0}>
                    <Grid item xs={12}>
                      <Box ml={8} mr={8}>
                        <Grid container spacing={2}>
                          {this.renderDivider(1)}
                          {this.renderTitle(printData)}
                          <table id="report" className="report-container">
                            <thead className="report-header">
                              {this.renderSummaryHeader(isRTL)}
                            </thead>
                            <tbody className="report-content">
                              {summaryRows?.map((item: any, index: number) =>
                                this.renderSummaryRow(item, index),
                              )}
                              {this.renderSummaryTotals(summaryRows, isRTL)}
                            </tbody>
                          </table>

                          <Grid item xs={12}>
                            <Typography variant="h5" style={{ marginTop: 10 }}>
                              {isRTL ? 'تفاصيل الحركات' : 'Transaction Details'}
                            </Typography>
                          </Grid>

                          <table id="report" className="report-container">
                            <thead className="report-header">
                              {this.renderDetailsHeader(isRTL)}
                            </thead>
                            <tbody className="report-content">
                              {rows?.map((item: any) =>
                                this.renderDetailsRow(item, isRTL),
                              )}
                            </tbody>
                          </table>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot className="report-footer">
          <tr>
            <td className="report-footer-cell">
              <div className="footer-info">
                <div className={'page-footer'}></div>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    );
  }
}

export default AdvanceReportPrint;